import React, { useState } from "react";

import {
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Skeleton,
  Box,
  Typography,
  Tooltip
} from "@mui/material";

import Pagination from "./Pagination";

// Generic paginated data table shared across list pages (Employees, Tickets, etc.).
// `columns`: [{ key, label, width, align, render(row) }] - `render` falls back to row[key].
function DataTable({
  columns,
  rows,
  loading = false,
  rowKey = (row) => row.id,
  selectable = false,
  selectedIds = [],
  onSelectionChange,
  onRowClick,
  emptyState,
  rowsPerPage = 50,
  pagination = true,
  isRowSelectable = () => true,
  rowSelectableTooltip = () => undefined
}) {
  const [page, setPage] = useState(1);

  const startIndex = pagination ? (page - 1) * rowsPerPage : 0;
  const pagedRows = pagination ? rows.slice(startIndex, startIndex + rowsPerPage) : rows;
  const totalPages = pagination ? Math.ceil(rows.length / rowsPerPage) : 1;

  const selectableRows = rows.filter(isRowSelectable);
  const allSelected = selectableRows.length > 0 && selectedIds.length === selectableRows.length;

  const isSelected = (id) => selectedIds.includes(id);

  const toggleSelectAll = (checked) => {
    onSelectionChange?.(checked ? selectableRows.map(rowKey) : []);
  };

  const toggleSelectOne = (id, checked) => {
    onSelectionChange?.(
      checked ? [...selectedIds, id] : selectedIds.filter((existingId) => existingId !== id)
    );
  };

  const columnCount = columns.length + (selectable ? 1 : 0);

  return (
    <Box>
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          boxShadow: "0 2px 10px rgba(0,0,0,0.05)"
        }}
      >
        <MuiTable>
          <TableHead>
            <TableRow sx={{ bgcolor: "background.default" }}>
              {selectable && (
                <TableCell sx={{ width: 56 }}>
                  <Checkbox
                    checked={allSelected}
                    indeterminate={selectedIds.length > 0 && !allSelected}
                    onChange={(event) => toggleSelectAll(event.target.checked)}
                  />
                </TableCell>
              )}

              {columns.map((column) => (
                <TableCell
                  key={column.key}
                  align={column.align}
                  sx={{ width: column.width, fontWeight: 600, fontSize: 13, color: "#475569" }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading && Array.from({ length: Math.min(rowsPerPage, 8) }).map((_, index) => (
              <TableRow key={`skeleton-${index}`}>
                {selectable && (
                  <TableCell>
                    <Skeleton variant="circular" width={24} height={24} />
                  </TableCell>
                )}
                {columns.map((column) => (
                  <TableCell key={column.key}>
                    <Skeleton variant="text" width="80%" height={24} />
                  </TableCell>
                ))}
              </TableRow>
            ))}

            {!loading && pagedRows.map((row) => {
              const id = rowKey(row);
              return (
                <TableRow
                  key={id}
                  hover
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  sx={{
                    cursor: onRowClick ? "pointer" : "default",
                    "& td": { borderBottom: "1px solid", borderColor: "divider" }
                  }}
                >
                  {selectable && (
                    <TableCell onClick={(event) => event.stopPropagation()}>
                      {isRowSelectable(row) ? (
                        <Checkbox
                          checked={isSelected(id)}
                          onChange={(event) => toggleSelectOne(id, event.target.checked)}
                        />
                      ) : (
                        <Tooltip title={rowSelectableTooltip(row) || ""}>
                          <span>
                            <Checkbox checked={false} disabled />
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  )}

                  {columns.map((column) => (
                    <TableCell key={column.key} align={column.align}>
                      {column.render ? column.render(row) : row[column.key]}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}

            {!loading && pagedRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={columnCount} sx={{ py: 6 }}>
                  <Box sx={{ textAlign: "center" }}>
                    {emptyState?.icon && (
                      <Box
                        sx={{
                          width: 58,
                          height: 58,
                          mx: "auto",
                          mb: 1.8,
                          borderRadius: "50%",
                          background: "#dbeafe",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#1d4ed8"
                        }}
                      >
                        <emptyState.icon sx={{ fontSize: 30 }} />
                      </Box>
                    )}

                    <Typography variant="h6" sx={{ color: "#1e3a8a", fontWeight: 700, mb: 0.7 }}>
                      {emptyState?.title || "No results found"}
                    </Typography>

                    {emptyState?.subtitle && (
                      <Typography sx={{ color: "#475569", fontSize: 14 }}>
                        {emptyState.subtitle}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </MuiTable>
      </TableContainer>

      {pagination && !loading && rows.length > 0 && (
        <Pagination
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          rangeStart={startIndex + 1}
          rangeEnd={Math.min(startIndex + rowsPerPage, rows.length)}
          totalCount={rows.length}
        />
      )}
    </Box>
  );
}

export default DataTable;
